<template>
    <div v-if="erro" class="alert alert-danger mt-3" role="alert">{{ erro }}</div>
    <div v-if="venda.id" class="card card-body shadow-sm mt-3">
      <!-- Cabeçalho -->
      <div class="form-header d-flex justify-content-between align-items-center">
        <h4 class="form-title">Detalhes da Venda nº {{ venda.id }}</h4>
        <button class="btn btn-secondary" @click="voltar">Voltar</button>
      </div>
  
      <!-- Dados do Cliente -->
      <FormSection title="Dados do Cliente">
        <div class="info-group">
          <strong>Cliente:</strong> {{ venda.cliente_nome }}
        </div>
        <div class="info-group">
            <strong>Pendente:</strong>
            <span :class="venda.pendente ? 'text-danger' : 'text-success'">
                {{ venda.pendente ? ' Sim' : ' Não' }}
            </span>
        </div>
      </FormSection>
  
      <!-- Itens da Venda -->
      <FormSection title="Itens de Venda">
        <div class="table-responsive">
          <table class="table table-bordered mb-0">
            <thead class="table-light">
              <tr>
                <th>Produto</th>
                <th>Qtd.</th>
                <th>Preço Unit.</th>
                <th>Subtotal</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, i) in itens" :key="i">
                <td>{{ item.produto_texto }}</td>
                <td>{{ item.quantidade }}</td>
                <td>{{ globalUtils.formatarValor(item.preco_unit) }}</td>
                <td>{{ globalUtils.formatarValor(item.subtotal) }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <th colspan="3" class="text-end">Total:</th>
                <th>{{ globalUtils.formatarValor(totalVenda) }}</th>
              </tr>
            </tfoot>
          </table>
        </div>
      </FormSection>
  
      <!-- Parcelas / Pagamentos -->
      <FormSection v-if="!venda.pendente" title="Detalhes do Pagamento">
        <!-- <div class="info-group mb-2">
          <strong>Parcelas:</strong> {{ venda.parcelas }}
        </div> -->
        <div class="table-responsive">
          <table class="table table-bordered">
            <thead class="table-light">
              <tr>
                <th>Parcela</th>
                <th>Forma de Pagamento</th>
                <th>Vencimento</th>
                <th>Valor</th>
                <th>Pagamento</th>
                <th>Conta Financeira</th>
                <th v-if="showChequeCols">Banco</th>
                <th v-if="showChequeCols">Agência</th>
                <th v-if="showChequeCols">Conta</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(p, i) in pagamentos" :key="i">
                <td>{{ i + 1 }}</td>
                <td>{{ p.forma }}</td>
                <td>{{ globalUtils.formatarData(p.data_vencimento) }}</td>
                <td>{{ globalUtils.formatarValor(p.valor) }}</td>
                <td>{{ p.data_pagamento ? globalUtils.formatarData(p.data_pagamento) : '-' }}</td>
                <td>{{ p.conta_finance }}</td>
                <td v-if="showChequeCols">{{ p.banco_numero || '-' }}</td>
                <td v-if="showChequeCols">{{ p.agencia || '-' }}</td>
                <td v-if="showChequeCols">{{ p.cheque_numero || '-' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </FormSection>
 
      <div class="mt-3">
        <em>Venda realizada por: {{ venda.por || 'Desconhecido' }}</em>
      </div>

    </div>
  
    <div v-else-if="!erro">
      <LoadingSpinner />
    </div>
  </template>
  
  <script setup>
  import { reactive, ref, onMounted, computed } from 'vue';
  import { useRouter, useRoute } from 'vue-router';
  import axios from '@/services/axios';
  import FormSection from '@/components/Layout/FormSection.vue';
  import globalUtils from '@/utils/globalUtils';
  import LoadingSpinner from '@/utils/LoadingSpinner.vue';
  
  const router = useRouter();
  const route = useRoute();
  const erro = ref('');
  
  const showChequeCols = computed(() =>
    pagamentos.value.some(p => p.forma.toLowerCase() === 'cheque')
  );

  // estado principal
  const venda = reactive({ id: null, cliente_nome: '', pendente: false, parcelas: 1, por: '' });
  const itens = ref([]);
  const pagamentos = ref([]);
  
  // carregar venda
  async function carregarVenda(id) {
    const { data } = await axios.get(`/loja/vendas/${id}`);
    Object.assign(venda, {
      id: data.id,
      cliente_nome: data.cliente_nome,
      pendente: data.pendente,
      parcelas: data.parcelas,
      por: data.por
    });
  }
  
  // Carrega todos os itens com os rótulos associados ao registro
  async function carregarItens(id) {
    const lista = [];
    let page = 1;
    let totalPages;
    do {
      const resp = await axios.get('/loja/vendas-itens', { params: { venda_id: id, page } });
      lista.push(...resp.data.data);
      totalPages = resp.data.totalPages;
      page += 1;
    } while (page <= totalPages);
    itens.value = lista.map(i => {
      return {
        produto_texto: [i.produto_codigo, i.produto_nome].filter(Boolean).join(' – ') || '—',
        quantidade:    i.quantidade,
        preco_unit:    parseFloat(i.preco_unit),
        subtotal:      parseFloat(i.subtotal)
      };
    });
  }
  
  // Carrega pagamentos com forma e apelido da conta
  async function carregarPagamentos(id) {
    const resp = await axios.get(`/loja/vendas-pagamentos/${id}`);
    const lista = resp.data ?? [];
    pagamentos.value = lista.map(p => {
      return {
        forma:            p.forma_pagamento || '—',
        data_vencimento:  p.data_vencimento,
        valor:            Number(p.valor),
        data_pagamento:   p.data_pagamento,
        banco_numero:     p.banco_numero,
        agencia:          p.agencia,
        cheque_numero:    p.cheque_numero,
        conta_finance:    p.conta_financeira_apelido || '—'
      };
    });
  }
  
  // totais
  const totalVenda = computed(() =>
    itens.value.reduce((sum, i) => sum + i.subtotal, 0)
  );
//   const totalPago = computed(() =>
//     pagamentos.value.reduce((sum, p) => sum + p.valor, 0)
//   );
//   const saldoRestante = computed(() => totalVenda.value - totalPago.value);
  
  onMounted(async () => {
    try {
      if (!route.params.id) throw new Error('ID ausente');
      await carregarVenda(route.params.id);
      await carregarItens(route.params.id);
      if (!venda.pendente) await carregarPagamentos(route.params.id);
    } catch (error) {
      const status = error.response?.status;
      erro.value = status === 403 ? 'Sem permissão para consultar os detalhes da venda.'
        : status === 404 ? 'Venda não encontrada.'
        : 'Não foi possível carregar todos os detalhes da venda. Tente novamente.';
    }
  });

  function voltar() {
    router.push('/loja/vendas');
  }
  </script>
    
  <style scoped>
  .info-group {
    margin-bottom: 0.5rem;
  }
  .form-header {
    margin-bottom: 1rem;
  }
  .totais .text-danger {
    font-weight: bold;
  }
  </style>
  